import { useEffect, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { getImageUrl } from "src/Common/common";
import { isAccountant, isClericalOver } from "src/Common/function";
import useTicketUnreadIndicator from "src/hooks/useTicketUnreadIndicator";
import UserService from "src/Services/API/user.service";
import UserFormPermissionService from "src/Services/API/userformpermission.service";

/**
 * SideMenu — The fixed left sidebar navigation for the application.
 *
 * Always mounted while the user is authenticated. Renders navigation items for
 * Work Orders, Purchase Orders, Recommendations, Customers, Calendar,
 * and Ticket Management (or "Report Bug" for non-Super Admin users).
 *
 * Highlights the active menu item based on the current URL pathname.
 *
 * Integrates with useTicketUnreadIndicator to display a red dot (8x8px circle)
 * to the right of the "Ticket Management" / "Report Bug" label whenever the user
 * has unread ticket activity. The red dot appears in real-time when a new ticket
 * message arrives and disappears when the user reads all unread tickets.
 *
 * Because this component is always mounted, the useTicketUnreadIndicator hook
 * maintains a persistent WebSocket subscription to the "ticket-notifications"
 * room, ensuring notifications are never missed regardless of which page the
 * user is on.
 */
function SideMenu() {
  const navigate = useNavigate();
  const location = useLocation();
  const currentUser = UserService.getCurrentUser();
  const isSuperAdmin = currentUser?.roles?.[0]?.name === "Super Admin";
  const mainHighlightColor = "bg-blueColor";
  const bottomHighlightColor = "bg-menuHighlightColor";
  const [formsExpanded, setFormsExpanded] = useState(false);
  const [isFormEnabled, setIsFormEnabled] = useState(false);
  /**
   * hasUnreadTicket: boolean from useTicketUnreadIndicator.
   * true when at least one ticket has unread messages or has never been opened.
   * Drives the red dot indicator on the ticket menu item.
   */
  const hasUnreadTicket = useTicketUnreadIndicator();

  useEffect(() => {
    if (!currentUser?.id) return;
    UserFormPermissionService.get(currentUser.id)
      .then((response) => {
        if (response.status === 200 || response.status === 201) {
          setIsFormEnabled(!!response.data?.isFormEnabled);
        }
      })
      .catch(() => {
        setIsFormEnabled(false);
      });
  }, [currentUser?.id]);

  const getBgColorArray = (activeIndex) => {
    const colors = Array(9).fill("bg-transparent");
    if (activeIndex >= 0) {
      const isBottomItem = activeIndex === 7;
      colors[activeIndex] = isBottomItem ? bottomHighlightColor : mainHighlightColor;
    }
    return colors;
  };

  const [bgColorArray, setBgColorArray] = useState(getBgColorArray(-1));
  const isActive = (index) => bgColorArray[index] !== "bg-transparent";
  const getTextColorClass = (index) => {
    return isActive(index) ? "text-white" : "text-black";
  };
  const getHoverClass = (index) => {
    return bgColorArray[index] === "bg-transparent" ? "hover:bg-menuHighlightColor" : "";
  };
  const getIcon = (index, whiteIcon, blackIcon) => {
    return getImageUrl(isActive(index) ? whiteIcon : blackIcon);
  };
  const formsActive = location.pathname.includes("/form-templates") || location.pathname.includes("/all-forms");

  useEffect(() => {
    if (location.pathname.includes("/calendar")) {
      setBgColorArray(getBgColorArray(4));
    } else if (location.pathname.includes("/work")) {
      setBgColorArray(getBgColorArray(0));
    } else if (location.pathname.includes("/purchase")) {
      setBgColorArray(getBgColorArray(1));
    } else if (location.pathname.includes("/recommendations")) {
      setBgColorArray(getBgColorArray(2));
    } else if (location.pathname.includes("/customer")) {
      setBgColorArray(getBgColorArray(3));
    } else if (location.pathname.includes("/calendar")) {
      setBgColorArray(getBgColorArray(4));
    } else if (location.pathname.includes("/team")) {
      setBgColorArray(getBgColorArray(5));
    } else if (location.pathname.includes("/time-off")) {
      setBgColorArray(getBgColorArray(6));
    } else if (location.pathname.includes("/report")) {
      setBgColorArray(getBgColorArray(8));
    } else if (location.pathname.includes("/form-templates") || location.pathname.includes("/all-forms")) {
      setBgColorArray(getBgColorArray(-1));
      setFormsExpanded(true);
    } else if (location.pathname.includes("/userguides")) {
      setBgColorArray(getBgColorArray(6));
    } else if (location.pathname.includes("/ticket")) {
      setBgColorArray(getBgColorArray(7));
    } else {
      setBgColorArray(getBgColorArray(-1));
    }
  }, [location.pathname]);

  const workOrderClicked = () => {
    if (!location.pathname.includes("/work")) {
      navigate("/work");
      setBgColorArray(getBgColorArray(0));
    }
  };

  const purchaseOrderClicked = () => {
    if (!location.pathname.includes("/purchase")) {
      navigate("/purchase");
      setBgColorArray(getBgColorArray(1));
    }
  };

  const recommendationsClicked = () => {
    navigate("/recommendations");
    setBgColorArray(getBgColorArray(2));
  };

  const customerClicked = () => {
    navigate("/customer");
    setBgColorArray(getBgColorArray(3));
  };

  const calendarClicked = () => {
    navigate("/calendar");
    setBgColorArray(getBgColorArray(4));
  };

  const teamClicked = () => {
    navigate("/team/manage");
    setBgColorArray(getBgColorArray(5));
  };

  const timeOffClicked = () => {
    navigate("/time-off");
    setBgColorArray(getBgColorArray(6));
  };

  const reportCenterClicked = () => {
    navigate("/report/ad-hoc");
    setBgColorArray(getBgColorArray(5));
  };

  const formsToggle = () => {
    setFormsExpanded((prev) => !prev);
  };

  const templatesClicked = () => {
    navigate("/form-templates");
    setBgColorArray(getBgColorArray(-1));
  };

  const allFormsClicked = () => {
    navigate("/all-forms");
    setBgColorArray(getBgColorArray(-1));
  };

  const userGuidesClicked = () => {
    navigate("/userguides");
    setBgColorArray(getBgColorArray(6));
  };

  const ticketClicked = () => {
    navigate("/ticket");
    setBgColorArray(getBgColorArray(7));
  };

  return (
    <div className="w-[257px] h-screen fixed top-0 left-0 bg-backgroundColor border-r-[1px] border-borderColor overflow-y-auto flex flex-col">
      <div className="w-full h-[80px] flex items-center px-[30px] mt-7">
        <img className="aspect-auto w-[190px]" src={getImageUrl("/images/logos/Black logo - no background.png")} />
      </div>

      <div className="w-full flex flex-1 pl-5 pr-1 flex-col mt-5 gap-1">
        <div
          className={`flex items-center h-[48px] gap-2 cursor-pointer px-[12px] rounded-3xl ${getHoverClass(0)} ${bgColorArray[0]}`}
          onClick={workOrderClicked}
        >
          <img
            className="aspect-auto w-[18px]"
            src={getIcon(0, "/images/sidemenu/white_wo.png", "/images/sidemenu/black_wo.png")}
          />
          <div className={`text-inputEmailTitle text-[16px] font-OpenSansRegular font-[600] ${getTextColorClass(0)}`}>
            Work Orders
          </div>
        </div>

        <div
          className={`flex items-center h-[48px] gap-2 cursor-pointer px-[12px] rounded-3xl ${getHoverClass(1)} ${bgColorArray[1]}`}
          onClick={purchaseOrderClicked}
        >
          <img
            className="aspect-auto w-[18px]"
            src={getIcon(1, "/images/sidemenu/white_po.png", "/images/sidemenu/black_po.svg")}
          />
          <div className={`text-inputEmailTitle text-[16px] font-OpenSansRegular font-[600] ${getTextColorClass(1)}`}>
            Purchase Orders
          </div>
        </div>

        <div
          className={`flex items-center h-[48px] gap-2 cursor-pointer px-[12px] rounded-3xl ${getHoverClass(2)} ${bgColorArray[2]}`}
          onClick={recommendationsClicked}
        >
          <img
            className="aspect-auto w-[18px]"
            src={getIcon(2, "/images/sidemenu/white_recommendation.png", "/images/sidemenu/black_recommendations.png")}
          />
          <div className={`text-inputEmailTitle text-[16px] font-OpenSansRegular font-[600] ${getTextColorClass(2)}`}>
            Recommendations
          </div>
        </div>

        <div
          className={`flex items-center h-[48px] gap-2 cursor-pointer px-[12px] rounded-3xl ${getHoverClass(3)} ${bgColorArray[3]}`}
          onClick={customerClicked}
        >
          <img
            className="aspect-auto w-[18px]"
            src={getIcon(3, "/images/sidemenu/white_customer.png", "/images/sidemenu/black_customer.png")}
          />
          <div className={`text-inputEmailTitle text-[16px] font-OpenSansRegular font-[600] ${getTextColorClass(3)}`}>
            Customers
          </div>
        </div>
        <div
          className={`flex items-center h-[48px] gap-2 cursor-pointer px-[12px] rounded-3xl ${getHoverClass(4)} ${bgColorArray[4]}`}
          onClick={calendarClicked}
        >
          <img
            className="aspect-auto w-[18px]"
            src={getIcon(4, "/images/sidemenu/white_date.png", "/images/sidemenu/black_date.png")}
          />
          <div className={`text-inputEmailTitle text-[16px] font-OpenSansRegular font-[600] ${getTextColorClass(4)}`}>
            Calendar
          </div>
        </div>

        <div
          className={`flex items-center h-[48px] gap-2 cursor-pointer px-[12px] rounded-3xl ${getHoverClass(6)} ${bgColorArray[6]}`}
          onClick={timeOffClicked}
        >
          <img
            className="aspect-auto w-[18px]"
            src={getIcon(6, "/images/sidemenu/white_timeoff.svg", "/images/sidemenu/black_timeoff.svg")}
          />
          <div className={`text-inputEmailTitle text-[16px] font-OpenSansRegular font-[600] ${getTextColorClass(6)}`}>
            Time Off Requests
          </div>
        </div>

        <div className="px-[12px] mt-3 mb-1 text-[#868DA6] font-OpenSansRegular text-[12px] not-italic font-[600] leading-[18px] tracking-[0.12px] uppercase">
          MANAGEMENT
        </div>

        <div
          className={`flex items-center h-[48px] gap-2 cursor-pointer px-[12px] rounded-3xl ${getHoverClass(5)} ${bgColorArray[5]}`}
          onClick={teamClicked}
        >
          <img
            className="aspect-auto w-[18px]"
            src={getIcon(5, "/images/sidemenu/white_team.svg", "/images/sidemenu/black_team.svg")}
          />
          <div className={`text-inputEmailTitle text-[16px] font-OpenSansRegular font-[600] ${getTextColorClass(5)}`}>
            Team
          </div>
        </div>

        {(isClericalOver() || isAccountant()) && (
          <div
            className={`flex items-center h-[48px] gap-2 cursor-pointer px-[12px] rounded-3xl ${getHoverClass(8)} ${bgColorArray[8]}`}
            onClick={reportCenterClicked}
          >
            <img
              className="aspect-auto w-[18px]"
              src={getIcon(8, "/images/sidemenu/white_report.svg", "/images/sidemenu/black_report.svg")}
            />
            <div className={`text-inputEmailTitle text-[16px] font-OpenSansRegular font-[600] ${getTextColorClass(8)}`}>
              Reports
            </div>
          </div>
        )}

        {isFormEnabled && (
          <div>
            <div
              className={`flex items-center h-[48px] gap-2 cursor-pointer px-[12px] rounded-3xl ${formsActive ? mainHighlightColor : "bg-transparent hover:bg-menuHighlightColor"}`}
              onClick={formsToggle}
            >
              <img
                className="aspect-auto w-[18px]"
                src={getImageUrl(formsActive ? "/images/sidemenu/white_lists.png" : "/images/sidemenu/black_lists.png")}
              />
              <div
                className={`text-inputEmailTitle text-[16px] font-OpenSansRegular font-[600] flex-1 ${formsActive ? "text-white" : "text-black"}`}
              >
                Forms
              </div>
              <svg
                className={`w-[14px] h-[14px] transition-transform ${formsExpanded ? "" : "rotate-180"}`}
                viewBox="0 0 24 24"
                fill="none"
                stroke={formsActive ? "#ffffff" : "#000000"}
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <polyline points="18 15 12 9 6 15" />
              </svg>
            </div>
            {formsExpanded && (
              <div className="flex flex-col gap-1 mt-1 ml-[26px]">
                <div
                  className={`flex items-center h-[48px] cursor-pointer px-[12px] rounded-3xl ${location.pathname.includes("/form-templates") ? mainHighlightColor : "bg-transparent hover:bg-menuHighlightColor"}`}
                  onClick={templatesClicked}
                >
                  <div
                    className={`text-inputEmailTitle text-[16px] font-OpenSansRegular font-[600] ${location.pathname.includes("/form-templates") ? "text-white" : "text-black"}`}
                  >
                    Templates
                  </div>
                </div>
                <div
                  className={`flex items-center h-[48px] cursor-pointer px-[12px] rounded-3xl ${location.pathname.includes("/all-forms") ? mainHighlightColor : "bg-transparent hover:bg-menuHighlightColor"}`}
                  onClick={allFormsClicked}
                >
                  <div
                    className={`text-inputEmailTitle text-[16px] font-OpenSansRegular font-[600] ${location.pathname.includes("/all-forms") ? "text-white" : "text-black"}`}
                  >
                    All Forms
                  </div>
                </div>
              </div>
            )}
          </div>
        )}
      </div>

      <div className="w-full flex flex-col pl-5 pr-1 pb-6 gap-1 mt-auto">
        {/* <div
          className={`flex items-center h-[48px] gap-2 cursor-pointer px-[12px] rounded-3xl ${bgColorArray[5]}`}
          onClick={userGuidesClicked}
        >
          <img className="aspect-auto w-[18px]" src={getImageUrl("/images/sidemenu/help/video.png")} />
          <div className={`text-inputEmailTitle text-[16px] font-OpenSansRegular font-[600] ${getTextColorClass(6)}`}>
            User Guides
          </div>
        </div> */}
        <div
          className={`flex items-center h-[48px] gap-2 cursor-pointer px-[12px] rounded-3xl ${getHoverClass(7)} ${bgColorArray[7]}`}
          onClick={ticketClicked}
        >
          <img className="aspect-auto w-[18px]" src={getImageUrl("/images/sidemenu/help/info.png")} />
          <div className={`text-inputEmailTitle text-[16px] font-OpenSansRegular font-[600] ${getTextColorClass(7)}`}>
            {isSuperAdmin ? "Ticket Management" : "Report Bug"}
          </div>
          {/* Red dot indicator: visible when the user has at least one unread ticket */}
          {hasUnreadTicket && <span className="ml-auto w-[8px] h-[8px] rounded-full bg-red-500 shrink-0" />}
        </div>
      </div>
    </div>
  );
}

export default SideMenu;
